<template>
  <tr>
    <td align="left" :height="contentModel.style.height" :style="rootStyles">
      <template v-if="!isBuilder">
        <table
          border="0"
          cellpadding="0"
          cellspacing="0"
          role="presentation"
          width="100%"
        >
          <tr>
            <td height="1" :style="contentStyles"></td>
          </tr>
        </table>
      </template>
      <template v-else>
        <ak-popover
          theme="dark"
          placement="top"
          width="325px"
          size="normal"
          class="block"
        >
          <template #trigger>
            <div
              class="flex flex--center"
              :style="{ height: contentModel.style.height + 'px' }"
            >
              <table
                border="0"
                cellpadding="0"
                cellspacing="0"
                role="presentation"
                width="100%"
                style="margin-bottom: 10px"
              >
                <tr>
                  <td height="1" :style="contentStyles"></td>
                </tr>
              </table>
            </div>
          </template>
          <template #popover>
            <ak-form style="font-size: 1rem">
              <ak-field label="Spacing Height" has-dark-bg class="mb-small">
                <ak-range
                  v-model:value="contentModel.style.height"
                  :min="10"
                  :show-bubbles="false"
                  :step="10"
                />
              </ak-field>
              <ak-pack>
                <ak-pack-item grow>
                  <ak-field label="Divider Style" has-dark-bg class="mb-small">
                    <ak-select
                      v-model="borderStyle"
                      :options="borderStyles"
                      track-by="value"
                      label="label"
                      @select="selectDividerStyle"
                    >
                    </ak-select>
                  </ak-field>
                </ak-pack-item>
                <ak-pack-item grow>
                  <ak-field label="Divider Colour" has-dark-bg class="mb-small">
                    <ak-color-picker
                      :color="contentModel.style.borderColor"
                      @change-color="changeColor"
                    />
                  </ak-field>
                </ak-pack-item>
              </ak-pack>
              <ak-field label="Divider Height" has-dark-bg class="mb-none">
                <ak-range
                  v-model:value="contentModel.style.borderWidth"
                  :max="30"
                  :show-bubbles="false"
                />
              </ak-field>
            </ak-form>
          </template>
        </ak-popover>
      </template>
    </td>
  </tr>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import config from '../../utils/config'
import { rgbaToString } from '@akerolabs/akero-vue/src/utils/helpers'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-divider></ak-email-template-divider>
   */
  name: 'AkEmailTemplateDivider',

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The content to render/edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        type: 'divider',
        style: {
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: 'rgb(238, 238, 238)',
          height: 20,
        },
      }),
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the content item is the last in its column
     */
    isLast: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Has flush spacing on the block
     */
    isFlush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Content index
     */
    contentIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description The width of the column
     */
    width: {
      type: Number,
      default: null,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const contentModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const borderStyles = ref([
      {
        label: 'None',
        value: 'none',
      },
      {
        label: 'Solid',
        value: 'solid',
      },
      {
        label: 'Dotted',
        value: 'dotted',
      },
      {
        label: 'Dashed',
        value: 'dashed',
      },
    ])

    /**
     * @returns {Object}
     * @description returns the root styles for the component
     */
    const rootStyles = computed(() => {
      return {
        fontSize: '0px',
        padding: '0',
        wordBreak: 'break-word',
        height: `${contentModel.value.style.height}px`,
        paddingTop:
          !props.isBuilder && props.contentIndex === 0 && !props.isFlush
            ? '10px'
            : '0',
        paddingBottom: props.isBuilder || props.isLast ? '0' : '10px',
      }
    })

    /**
     * @returns {Object}
     * @description returns the content styles for the component
     */
    const contentStyles = computed(() => {
      return {
        borderTopStyle: contentModel.value.style.borderStyle,
        borderTopWidth: `${contentModel.value.style.borderWidth}px`,
        borderTopColor: contentModel.value.style.borderColor,
        fontSize: 1,
        margin: '0 auto',
        width: '100%',
        verticalAlign: 'middle',
      }
    })

    const borderStyle = computed(() => {
      return borderStyles.value.find(
        (o) => o.value === contentModel.value.style.borderStyle,
      )
    })

    const changeColor = (color) => {
      contentModel.value.style.borderColor = rgbaToString(color)
    }

    const selectDividerStyle = (selected) => {
      contentModel.value.style.borderStyle = selected.value
    }

    return {
      borderStyles,
      rootStyles,
      contentStyles,
      borderStyle,
      changeColor,
      selectDividerStyle,
      contentModel,
    }
  },
})
</script>
